import { forEach, isEqual, isObject, merge } from 'lodash-es'

/**
 * Filters an object by keeping only the specified keys.
 *
 * @param {Object.<string, string | number>} object - The input object containing key-value pairs.
 * @param {string[]} keysToKeep - An array of keys to retain in the returned object.
 * @returns {Object.<string, string | number>} A new object containing only the specified keys.
 */
export const filterObject = (object: { [key: string]: string | number }, keysToKeep: string[]): { [key: string]: number | string } => {
  return Object.keys(object)
    .filter((k) => keysToKeep.includes(k))
    .reduce<{ [key: string]: number | string }>((acc, current) => {
      acc[current] = object[current]
      return acc
    }, {})
}

/**
 * Aggregates selected values from an object and optionally groups the rest under a custom "others" key.
 *
 * @template T - The key type of the input object.
 * @param {Record<T, number>} object - The input object with numeric values.
 * @param {T[]} keysToKeep - The list of keys to include in the returned object.
 * @param {T} othersKey - A special key to store the sum of all non-included values.
 * @returns {Record<T, number>} A new object with selected keys and optionally the aggregated "others" key.
 */
export const aggregateRawDataValues = <T extends string>(object: Record<T, number>, keysToKeep: T[], othersKey: T): Record<T, number> => {
  const data = filterObject(object, keysToKeep) as Record<T, number>

  if (keysToKeep.includes(othersKey)) {
    const othersValue = object[othersKey] ?? 0
    data[othersKey] = Object.keys(object)
      .filter((key): key is T => !keysToKeep.includes(key as T))
      .reduce((sum, key) => sum + object[key], othersValue)
  }

  return data
}

/**
 * Filters an object by selected keys and maps the "others" label if requested.
 *
 * @template T - The key type of the input object.
 * @param {Record<T, string>} object - The input object with string values.
 * @param {T[]} keysToKeep - The list of keys to retain in the returned object.
 * @param {T} othersKey - A special key to represent the "others" label.
 * @returns {Record<T, string>} A new object with selected keys and optionally the "others" label.
 */
export const aggregateRawDataMapping = <T extends string>(object: Record<T, string>, keysToKeep: T[], othersKey: T): Record<T, string> => {
  const data = filterObject(object, keysToKeep) as Record<T, string>

  if (keysToKeep.includes(othersKey)) {
    data[othersKey] = othersKey // Assign the "others" label
  }

  return data
}

const _diff = (left: Record<string, any>, right: Record<string, any>) => {
  const r: Record<string, any> = {}
  const compared = right || {}

  forEach(left, (v: any, k: string) => {
    if (isEqual(compared[k], v)) return
    r[k] = isObject(v) ? diff(v, compared[k]) : v
  })

  return r
}

export const diff = (obj1: Record<string, any>, obj2: Record<string, any>) => merge(_diff(obj1, obj2), _diff(obj2, obj1))
